/**
 * Cell renderers for ALN Sync — aligned with Angular `import-data` alnsync tables.
 */
import { Link } from 'react-router-dom'
import { formatPhoneDisplay } from '@/features/property/utils/formatPhone'
import { getUserManagementEditUrl } from '@/lib/utils/userManagementNavigation'
import type { AlnRow, ImportDataType } from '../types/importData'
import { ALN_SYNC_ACTION_KEY, listedToStatus } from './alnSyncTableColumns'
import {
  CompanyChangeCell,
  NonLinkText,
} from '../components/alnSyncTableCellParts'

function dash(v: unknown): string {
  if (v == null || v === '') return '—'
  return String(v)
}

function companyEditLink(id: unknown, label: unknown): React.ReactNode {
  const s = id != null && id !== '' ? String(id) : ''
  const text = label != null && label !== '' ? String(label) : '—'
  if (!s || s === '0') return <span className="text-slate-700">{text}</span>
  return (
    <Link to={`/company/edit/${s}`} className="text-button underline" target="_blank" rel="noreferrer">
      {text}
    </Link>
  )
}

function propertyEditLink(propertyId: unknown, label: unknown): React.ReactNode {
  const s = propertyId != null && propertyId !== '' ? String(propertyId) : ''
  const text = label != null && label !== '' ? String(label) : '—'
  if (!s || s === '0') return <span className="text-slate-700">{text}</span>
  return (
    <Link to={`/property/edit/${s}`} className="text-button underline" target="_blank" rel="noreferrer">
      {text}
    </Link>
  )
}

function userMgmtLink(userId: unknown, label: unknown): React.ReactNode {
  const s = userId != null && userId !== '' ? String(userId) : ''
  const text = label != null && label !== '' ? String(label) : '—'
  if (!s || s === '0') return <span className="text-slate-700">{text}</span>
  return (
    <a href={getUserManagementEditUrl(s)} target="_blank" rel="noopener noreferrer" className="text-button underline">
      {text}
    </a>
  )
}

function showSyncViewButton(importType: ImportDataType, row: AlnRow): boolean {
  switch (importType) {
    case 'management_company':
      return Boolean(row.previous_company_id && row.current_company_id)
    case 'property_name':
    case 'occupancy':
      return false
    default:
      return true
  }
}

export function renderAlnSyncCell(
  importType: ImportDataType,
  columnKey: string,
  row: AlnRow,
  onView: (r: AlnRow) => void,
): React.ReactNode {
  if (columnKey === ALN_SYNC_ACTION_KEY) {
    if (!showSyncViewButton(importType, row)) return null
    return (
      <button type="button" className="text-button hover:underline" onClick={() => onView(row)}>
        View
      </button>
    )
  }

  if (columnKey === 'listed') {
    return <span className="text-slate-700">{listedToStatus(row.listed)}</span>
  }

  if (columnKey === 'previous_mgmt' && importType === 'management_company') {
    return <CompanyChangeCell row={row} side="previous" />
  }
  if (columnKey === 'current_mgmt' && importType === 'management_company') {
    return <CompanyChangeCell row={row} side="current" />
  }

  if (columnKey === 'fk_property_id' && importType === 'property_name') {
    const id = row.fk_property_id ?? row.property_id
    return propertyEditLink(id, id)
  }

  if (columnKey === 'property_name') {
    const label = row.property_name ?? row.name
    return propertyEditLink(row.property_id, label)
  }

  if (columnKey === 'mgmt_company' && (importType === 'manager_change' || importType === 'area_supervisor')) {
    return companyEditLink(row.fk_company_id, row.mgmt_company)
  }

  if (
    columnKey === 'current_manager_email' ||
    columnKey === 'current_area_supervisor_email' ||
    columnKey === 'previous_area_supervisor_email'
  ) {
    return <NonLinkText>{dash(row[columnKey])}</NonLinkText>
  }

  if (columnKey === 'previous_manager_name' && importType === 'manager_change') {
    return userMgmtLink(row.previous_manager_user_id, row.previous_manager_name)
  }

  if (columnKey === 'current_manager_name' && importType === 'manager_change') {
    return <NonLinkText>{dash(row.current_manager_name)}</NonLinkText>
  }

  if (columnKey === 'previous_area_supervisor_name' && importType === 'area_supervisor') {
    return userMgmtLink(row.previous_manager_user_id, row.previous_area_supervisor_name)
  }

  if (columnKey === 'current_area_supervisor_name' && importType === 'area_supervisor') {
    return <NonLinkText>{dash(row.current_area_supervisor_name)}</NonLinkText>
  }

  if (columnKey === 'current_pmc_name' && importType === 'pmc_import') {
    return companyEditLink(row.fk_company_id, row.current_pmc_name)
  }

  if (columnKey === 'current_pmc_address_1' && importType === 'pmc_import') {
    const val = row.current_pmc_address_1 ?? row.current_pmc_address
    return <span className="text-slate-700">{dash(val)}</span>
  }

  if (columnKey === 'current_pmc_phone' && importType === 'pmc_import') {
    const raw = row.current_pmc_phone
    const formatted = formatPhoneDisplay(raw != null ? String(raw) : '')
    return <span className="text-slate-700">{formatted || '—'}</span>
  }

  if (columnKey === 'previous_property_name' && importType === 'property_name') {
    const text = row.previous_property_name ?? row.previous_name
    return <span className="text-slate-700">{dash(text)}</span>
  }

  if (columnKey === 'current_property_name') {
    if (importType === 'property_name') {
      const text = row.current_property_name ?? row.current_name
      return <span className="text-slate-700">{dash(text)}</span>
    }
    if (importType === 'property_import') {
      return propertyEditLink(row.property_id, row.current_property_name)
    }
    if (importType === 'occupancy') {
      return propertyEditLink(row.fk_property_id, row.current_property_name)
    }
  }

  if (columnKey === 'current_property_phone' && importType === 'property_import') {
    const raw = row.current_property_phone
    const formatted = formatPhoneDisplay(raw != null ? String(raw) : '')
    return <span className="text-slate-700">{formatted || '—'}</span>
  }

  if (columnKey === 'approved_at') {
    const raw = row.approved_at
    if (raw == null || raw === '') return <span className="text-slate-700">—</span>
    return <span className="text-slate-700">{String(raw)}</span>
  }

  if (columnKey === 'undone_status') {
    const raw = row.undone_status
    return <span className="text-slate-700">{raw ? String(raw) : ''}</span>
  }

  if (columnKey === 'approved_by') {
    const raw = row.approved_by
    return <span className="text-slate-700">{raw ? String(raw) : '—'}</span>
  }

  if (columnKey === 'uploaded_at') {
    const raw = row[columnKey]
    if (raw == null || raw === '') return <span className="text-slate-700">—</span>
    // Usually these are standard date strings or unix timestamps
    let d = new Date(raw as string | number)
    if (typeof raw === 'number' && raw > 0 && raw < 10000000000) {
      d = new Date(raw * 1000)
    }
    if (Number.isNaN(d.getTime())) return <span className="text-slate-700">{dash(raw)}</span>
    const formatted = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`
    return <span className="text-slate-700">{formatted}</span>
  }

  const v = row[columnKey]
  return <span className="text-slate-700">{dash(v)}</span>
}
